import Link from "next/link";
import { Suspense } from "react";
import { Bell, Menu } from "lucide-react";
import { Brandmark } from "@/components/brandmark";
import { SearchHero } from "@/components/search/search-hero";
import { UserMenu } from "@/components/auth/user-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  HeaderNavLink,
  HeaderDashboardTabLink,
  MobileNavLink,
  MobileDashboardTabLink,
} from "@/components/nav-link";

/** Dieselben 8 Hauptbereiche wie die Desktop-Nav unten, hier als eine flache Liste fuer
 * das mobile Hamburger-Menue - anders als dort OHNE die `hidden sm:`/`hidden lg:`-
 * Breakpoint-Abstufung, da im aufgeklappten Sheet ohnehin genug vertikaler Platz fuer
 * alle 8 Links ist (backlog.md -> [Punkt 98] "Mobile-Optimierung mit hoher Prioritaet",
 * "ca. 80% der Nutzer kommen ueber TikTok und landen zuerst in der mobilen Ansicht"). */
const MOBILE_NAV_LINKS: { href: string; label: string; dashboardTab?: "depot" | "watchlist" }[] = [
  { href: "/dashboard?tab=depot", label: "Mein Depot", dashboardTab: "depot" },
  { href: "/dividenden-tracker", label: "Meine Dividenden" },
  { href: "/meine-news", label: "Meine News" },
  { href: "/dashboard?tab=watchlist", label: "Watchlist", dashboardTab: "watchlist" },
  { href: "/entdecken", label: "Entdecken" },
  { href: "/aktienanalysen", label: "Premium Aktienanalysen" },
  { href: "/akademie", label: "Akademie" },
];

export function SiteHeader({
  symbol,
  showSearch = true,
}: {
  symbol?: string;
  /** Feedback-Runde 9 (2026-07-22): auf der Startseite steht bereits die grosse,
   * mittige SearchHero im Hero-Bereich darunter - die kompakte Nav-Suche hier waere
   * dort eine zweite, redundante Suchleiste. Auf allen anderen Seiten (ohne eigene
   * grosse Suche) bleibt sie wie gehabt sichtbar. */
  showSearch?: boolean;
}) {
  return (
    <header
      id="site-header"
      className="sticky top-0 z-30 border-b border-hairline bg-background/90 backdrop-blur"
    >
      <div className="mx-auto flex max-w-page items-center justify-between gap-4 px-4 py-3">
        <Link href="/" className="flex shrink-0 items-center gap-2.5">
          <Brandmark size={48} className="h-9 w-9" />
          <span className="wordmark hidden text-lg uppercase text-ink sm:inline">Erikonomie</span>
        </Link>
        {showSearch && (
          <Suspense>
            <SearchHero variant="compact" currentSymbol={symbol} />
          </Suspense>
        )}
        <div className="flex shrink-0 items-center gap-2">
          {/* Aktiver Bereich farblich hervorgehoben (Gold) - backlog.md -> "Aktueller
              Nav-Bereich muss in der Hauptnavigation sichtbar sein", Landingpage-Feedback
              2026-08-17 Punkt 14. Die beiden /dashboard-Tab-Links brauchen useSearchParams
              (siehe nav-link.tsx-Kommentar) und stehen deshalb in einer eigenen Suspense -
              Fallback ist die schlichte, unmarkierte Variante (auf allen Seiten außer
              /dashboard selbst ohnehin korrekt, da dort nie aktiv). */}
          <Suspense
            fallback={
              <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
                <Link href="/dashboard?tab=depot">Mein Depot</Link>
              </Button>
            }
          >
            <HeaderDashboardTabLink tab="depot" className="hidden sm:inline-flex">
              Mein Depot
            </HeaderDashboardTabLink>
          </Suspense>
          {/* Erst ab lg sichtbar (nicht sm wie die anderen) - 5 Textlinks nebeneinander
              wuerden auf mittelbreiten Desktop-Fenstern eng/umbrechen, dieser ist der
              neueste/am ehesten verzichtbare bei wenig Platz. */}
          <HeaderNavLink href="/dividenden-tracker" className="hidden lg:inline-flex">
            Meine Dividenden
          </HeaderNavLink>
          {/* Neu (2026-07-28, Erik: "großes Paket 'Meine News'") - bewusst dieselbe
              Breakpoint-Stufe wie "Meine Dividenden" (nicht die naechsthoehere Stufe),
              da Erik dies explizit als grosses, im Fokus stehendes Feature einordnet,
              nicht als Nebensaechlichkeit. */}
          <HeaderNavLink href="/meine-news" className="hidden lg:inline-flex">
            Meine News
          </HeaderNavLink>
          <Suspense
            fallback={
              <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
                <Link href="/dashboard?tab=watchlist">Watchlist</Link>
              </Button>
            }
          >
            <HeaderDashboardTabLink tab="watchlist" className="hidden sm:inline-flex">
              Watchlist
            </HeaderDashboardTabLink>
          </Suspense>
          {/* Vereint seit [Punkt 25+29+30] Screener + Themen + Sektor-Rangliste unter
              einer URL (app/entdecken/page.tsx) - vormals zwei separate Nav-Punkte
              ("Screener" + "Entdecken" -> /themen). */}
          <HeaderNavLink href="/entdecken" className="hidden sm:inline-flex">
            Entdecken
          </HeaderNavLink>
          {/* Neu (2026-07-23, Erik): zwischen Entdecken und Akademie - gleiche
              sm-Sichtbarkeitsstufe wie beide Nachbarn. Label war 2026-07-24 bis
              2026-08-19 auf "Aktienanalysen" verkuerzt (Platzgrund), seit der
              Entdecken-Zusammenlegung ([Punkt 31], Screener als eigener Nav-Punkt
              entfaellt) ist wieder Platz fuer den vollen Seitentitel. */}
          <HeaderNavLink href="/aktienanalysen" className="hidden sm:inline-flex">
            Premium Aktienanalysen
          </HeaderNavLink>
          {/* Ab sm sichtbar, gleiche Stufe wie "Entdecken" (Erik 2026-07-21: "dass der
              'Akademie' Bereich da ganz oben neben 'Entdecken' auftaucht") - explizite
              Erik-Anforderung, vorher bewusst zurueckhaltender bei xl platziert. */}
          <HeaderNavLink href="/akademie" className="hidden sm:inline-flex">
            Akademie
          </HeaderNavLink>
          {/* Icon statt Textlink (Nutzer-Feedback 2026-07-18, Wegfall der TabsList auf
              /dashboard): braucht dadurch einen eigenen Einstiegspunkt in der Hauptnav,
              ein Icon bleibt bei ohnehin knappem Platz kompakter als ein sechster
              Textlink. */}
          <Button variant="ghost" size="icon" asChild aria-label="Benachrichtigungen">
            <Link href="/dashboard?tab=benachrichtigungen">
              <Bell />
            </Link>
          </Button>
          {/* Hamburger-Menue (backlog.md -> [Punkt 98]) - unterhalb von sm verschwinden
              sonst ALLE Textlinks oben ersatzlos (nur `hidden sm:inline-flex`, kein
              Fallback), echte Telefone (die Mehrheit laut CLAUDE.md) hatten dadurch
              bisher gar keinen Zugang zur Hauptnavigation. Bewusst nur `sm:hidden`
              (nicht bis `lg`) - im sm-lg-Zwischenbereich sind bereits 6 von 8 Links
              sichtbar, kein vollstaendiger Blackout wie unterhalb von sm. */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Menü öffnen" className="sm:hidden">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <SheetTitle className="px-4 pt-4">Menü</SheetTitle>
              <nav className="flex flex-col gap-1 p-2">
                {MOBILE_NAV_LINKS.map((link) =>
                  link.dashboardTab ? (
                    <Suspense key={link.href} fallback={<span className="rounded-md px-3 py-2.5 text-sm font-medium text-ink">{link.label}</span>}>
                      <SheetClose asChild>
                        <MobileDashboardTabLink tab={link.dashboardTab}>{link.label}</MobileDashboardTabLink>
                      </SheetClose>
                    </Suspense>
                  ) : (
                    <SheetClose key={link.href} asChild>
                      <MobileNavLink href={link.href}>{link.label}</MobileNavLink>
                    </SheetClose>
                  ),
                )}
              </nav>
            </SheetContent>
          </Sheet>
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
